import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const AdvertisingRoi = () => {
  // State variables for Advertising ROI calculation
  const [revenue, setRevenue] = useState(0); // Revenue from Campaign
  const [campaignCost, setCampaignCost] = useState(0); // Cost of the Campaign
  const [roi, setRoi] = useState(0); // Calculated ROI

  // Event handlers to update state values
  const handleRevenueChange = (e) => setRevenue(parseFloat(e.target.value) || 0);
  const handleCampaignCostChange = (e) => setCampaignCost(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the ROI when any input changes
  useEffect(() => {
    if (revenue && campaignCost) {
      const calculatedROI = ((revenue - campaignCost) / campaignCost) * 100;
      setRoi(calculatedROI);
    } else {
      setRoi(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });


  }, [revenue, campaignCost]);

  return (
    <Layout title="Advertising ROI Calculator" path="advertising-roi">
      <section className="section-padding" id="advertising-roi-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Advertising ROI Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Advertising ROI Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Revenue from Campaign Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="revenue" className="form-label">
                        Revenue from Campaign (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Revenue:</strong> Enter the total revenue generated from the advertising campaign."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="revenue"
                        placeholder="Enter revenue generated from the campaign"
                        value={revenue || ""}
                        onChange={handleRevenueChange}
                      />
                    </div>

                    {/* Campaign Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="campaignCost" className="form-label">
                        Campaign Cost (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Campaign Cost:</strong> Enter the total cost of the advertising campaign."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="campaignCost"
                        placeholder="Enter total campaign cost"
                        value={campaignCost || ""}
                        onChange={handleCampaignCostChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Advertising ROI:{" "}
                        <span className="fw-bold ms-3">
                          {roi === 0 ? "0.00" : roi.toFixed(2)} %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Advertising ROI Calculator
                </h3>

                {/* What is an Advertising ROI Calculator? */}
                <h4 className="mt-3 mb-3">What is an Advertising ROI Calculator?</h4>
                <p>
                  The **Advertising ROI Calculator** measures the return on investment (ROI) for advertising campaigns. ROI represents the percentage return on the money spent on a campaign. By inputting the revenue generated and the total cost of the campaign, you can quickly assess whether your marketing efforts are financially profitable.
                </p>

                {/* Why Use an Advertising ROI Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Advertising ROI Calculator?</h4>
                <p>
                  This calculator helps businesses determine how much revenue they are generating relative to their advertising expenditure. It helps evaluate the success of advertising campaigns by showing if they are delivering a positive return, ensuring that marketing budgets are being spent effectively. An ROI greater than 100% indicates a profitable campaign.
                </p>

                {/* Benefits of Using an Advertising ROI Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Advertising ROI Calculator</h4>
                <ul>
                  <li><strong>Provides insight into advertising effectiveness:</strong> Helps understand the performance of your marketing campaigns and whether they are worth the investment.</li>
                  <li><strong>Helps businesses optimize marketing spending:</strong> Allows businesses to identify underperforming campaigns and reallocate funds toward more profitable marketing efforts.</li>
                  <li><strong>Essential for evaluating digital marketing ROI:</strong> In the world of online advertising, ROI tracking is essential to assess how well digital campaigns are driving sales and revenue.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Advertising ROI Calculator?</h4>
                <p>
                  The Advertising ROI Calculator is ideal for:
                </p>
                <ul>
                  <li><strong>Marketing Agencies:</strong> To track the performance of campaigns they run for clients and optimize the return on investment.</li>
                  <li><strong>E-commerce:</strong> To measure the success of online ads and ensure advertising costs are covered by the revenue generated.</li>
                  <li><strong>Media Companies:</strong> To assess the revenue generated through advertisements and determine if they are maximizing returns.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is considered a good ROI for advertising campaigns?</h4>
                <p>
                  A good ROI for advertising campaigns is typically considered to be above 100%, meaning that your revenue exceeds the campaign cost. However, higher ROI percentages (200% or more) generally indicate highly profitable campaigns.
                </p>

                <h4 className="mt-4 mb-3">What factors can affect advertising ROI?</h4>
                <p>
                  Several factors can impact advertising ROI, including the targeting accuracy of the campaign, the ad platform used, the timing of the campaign, the quality of the content, and the competitive environment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AdvertisingRoi;
